import { Callout, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { useAggregateAPIOperationStatus } from "@octopusdeploy/octopus-react-client";
import type { AnonymousVcsCredentials, GitHubVcsCredentials, PlatformHubVersionControlSettingsResource, ReferenceVcsCredentials, Repository, UsernamePasswordVcsCredentials } from "@octopusdeploy/octopus-server-client";
import { defaultUsernamePasswordVcsCredentials, AuthenticationType, IsUsingAnonymousAuth, IsUsingUsernamePasswordAuth, Permission } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { PageContent } from "~/components/PageContent/PageContent";
import { ExpandableFormSection, Note, required, Sensitive, Summary, Text } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
interface PlatformHubConnectionInternalPageProps {
    loaderData: PlatformHubVersionControlSettingsPageLoaderData;
}
interface PlatformHubVersionControlSettingsPageLoaderData {
    persistenceSettings: PlatformHubVersionControlSettingsResource;
}
export async function platformHubVersionControlSettingsPageLoader(repository: Repository): Promise<PlatformHubVersionControlSettingsPageLoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    return { persistenceSettings };
}
export function PlatformHubVersionControlSettingsPage({ loaderData }: PlatformHubConnectionInternalPageProps) {
    const { isInProgress } = useAggregateAPIOperationStatus();
    const [isNew] = React.useState(!loaderData.persistenceSettings.Url);
    const { model, setModel, formProps, createSaveAction, getFieldError } = useForm({
        initialModel: loaderData.persistenceSettings,
        formName: "Save Platform Hub Configuration",
        onSubmit: async (repository: Repository, persistenceSettingsModel) => await repository.PlatformHub.modifyVersionControlSettings(persistenceSettingsModel),
        submitPermission: { permission: Permission.PlatformHubEdit },
    });
    const updatePersistenceSettings = (newValues: Partial<PlatformHubVersionControlSettingsResource>) => {
        setModel({ ...model, ...newValues });
    };
    const onCredentialTypeChanged = async (newAuthenticationType: AuthenticationType) => {
        if (newAuthenticationType === AuthenticationType.UsernamePassword) {
            updatePersistenceSettings({ Credentials: defaultUsernamePasswordVcsCredentials });
        }
        else if (newAuthenticationType === AuthenticationType.Anonymous) {
            updatePersistenceSettings({ Credentials: { Type: AuthenticationType.Anonymous } });
        }
    };
    const urlSummary = model.Url ? Summary.summary(model.Url) : Summary.placeholder("Enter a Git repository URL");
    const credentials = model.Credentials;
    const authSummary = createAuthSummary(credentials);
    return (<PageContent header={{ title: "Version Control Settings", primaryAction: createSaveAction() }}>
            <Form {...formProps} expandAllOnMount={isNew}>
                <ExpandableFormSection errorKey="Url" title="Git Repository" summary={urlSummary} help={"Add the Git repository to use with the platform hub"}>
                    <Callout type={"information"} title="Repository must be initialized">
                        <div>If you are creating a new repository, it must be initialized with at least one branch</div>
                    </Callout>
                    <Text key="Url" value={model.Url} onChange={(Url) => updatePersistenceSettings({ Url })} label="URL" error={getFieldError("Url")} validate={required("Enter a Git repository URL.")} autoFocus={true} accessibleName={"Git repository URL"}/>
                    <Note>
                        The HTTPS URL to your git repo. E.g. <code>https://github.com/acme/hello-world.git</code>
                    </Note>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="Authentication" title="Authentication" summary={authSummary} help="Choose a method to authenticate with the above Git repository">
                    <RadioButtonGroup value={credentials.Type} onChange={onCredentialTypeChanged} accessibleName={"Authentication mode"}>
                        <RadioButton value={AuthenticationType.UsernamePassword} key={AuthenticationType.UsernamePassword} label={"Username/Password"} accessibleName={"Authenticate using username and password"}/>
                        {IsUsingUsernamePasswordAuth(credentials) && (<>
                                <Text key="Username" value={credentials.Username} onChange={(Username) => updatePersistenceSettings({ Credentials: { ...credentials, Username } })} label="Username" error={getFieldError("Username")} validate={required("Enter authentication details.")}/>
                                <Sensitive key="Password" value={credentials.Password} onChange={(Password) => updatePersistenceSettings({ Credentials: { ...credentials, Password } })} label="Password or Personal Access Token" error={getFieldError("Password")}/>
                                <Note>If you are using a personal access token, ensure it is scoped with read and write access for the repo.</Note>
                            </>)}
                        <RadioButton value={AuthenticationType.Anonymous} key={AuthenticationType.Anonymous} label={"Anonymous"} accessibleName={"No authentication"}/>
                        {IsUsingAnonymousAuth(credentials) && <Note>When no credentials are required (e.g. locally hosted repositories).</Note>}
                    </RadioButtonGroup>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="BasePath" title="Git File Storage Directory" summary={Summary.summary(model.BasePath)} help="The directory where Octopus should store the files in the repository">
                    <Text inputProps={{
            startAdornment: (<>
                                    <code>.octopus</code>
                                    &nbsp;/
                                </>),
        }} key="BasePath" value={model.BasePath.substring(".octopus/".length)} onChange={(BasePath) => updatePersistenceSettings({ BasePath: `.octopus/${BasePath}` })} label="Git File Storage Directory" error={getFieldError("BasePath")} disabled={isInProgress} accessibleName={"Directory in the repository where files relating to the platform hub should be stored"}/>
                    <Note>This controls where in your repository the files related to the platform hub will be stored.</Note>
                    <Note>
                        It’s recommended to connect a dedicated Git repository to the platform hub, in this case the default directory, <code>.octopus</code>, is a good option. If managing multiple configurations within the same Git repository,
                        consider organizing them in subdirectories, e.g. <code>.octopus/platform-hub</code>.
                    </Note>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="DefaultBranch" title="Branch Settings" summary={Summary.summary(<span>
                            Default branch is <strong>{model.DefaultBranch}</strong>
                        </span>)} help="The default branch used in Platform Hub">
                    <Text key="DefaultBranch" value={model.DefaultBranch} onChange={(DefaultBranch) => updatePersistenceSettings({ DefaultBranch })} label="Default Branch" error={getFieldError("DefaultBranch")} validate={required("Enter a default branch.")} disabled={isInProgress} accessibleName={"Name of the default branch on the Git repository"}/>
                </ExpandableFormSection>
            </Form>
        </PageContent>);
}
function createAuthSummary(credentials: AnonymousVcsCredentials | UsernamePasswordVcsCredentials | ReferenceVcsCredentials | GitHubVcsCredentials) {
    let authSummary = Summary.placeholder("Enter authentication details");
    if (IsUsingUsernamePasswordAuth(credentials)) {
        const { Username: username, Password: password } = credentials;
        if (username && password && password.HasValue) {
            authSummary = Summary.summary(React.Children.toArray([
                <span>Authenticated with username&nbsp;</span>,
                <span>
                        <strong>{username}</strong>
                    </span>,
            ]));
        }
    }
    else if (IsUsingAnonymousAuth(credentials)) {
        authSummary = Summary.summary(React.Children.toArray([
            <span>
                    Authenticated as an <strong>Anonymous</strong> user
                </span>,
        ]));
    }
    return authSummary;
}
